<template>
  <v-row v-if="tableDate" align="center">
    <v-col cols="12">
      {{ computedValueTextField }}
    </v-col>
  </v-row>
  <div v-else-if="noGrid" style="display: flex">
    <span style="font-weight: bold" :class="addLabelPadding ? 'd-inline pt-1 pr-0' : 'd-inline  pr-1'"
      >{{ label }}:</span
    >
    <div v-if="isEditable">
      <div class="pl-3">
        <v-text-field
          :id="`dateField_${id}_Table`"
          hide-details="auto"
          placeholder="MM/DD/YYYY"
          class="pa-0 ma-0"
          :style="textStyle"
          :aria-describedby="`dateField_${id}_Table`"
          :aria-label="ariaLabel"
          v-model="computedValueTextField"
          @focus="isFocused = true"
          @blur="blur"
          :rules="
            isRequired
              ? [
                  (value) => {
                    return requiredRule(value);
                  },
                  (value) => {
                    return validDateFormatRule(value);
                  },
                  (value) => {
                    return validDateRangeRule(value, maximumDate, minimumDate);
                  },
                ]
              : [
                  (value) => {
                    return validDateFormatRule(value);
                  },
                  (value) => {
                    return validDateRangeRule(value, maximumDate, minimumDate);
                  },
                ]
          "
        >
          <template v-slot:append-inner>
            <DatePicker
              :invalidDates="invalidDates"
              :maxDate="maximumDate"
              :minDate="minimumDate"
              :required="isRequired"
              v-model="computedValue"
            />
          </template>
          <template v-if="appendWith" v-slot:append
            ><span>{{ appendWith }}</span></template
          >
        </v-text-field>
      </div>
    </div>
    <span v-else :class="addDatePadding ? 'd-inline pl-4 pt-1' : 'd-inline pl-4'"
      >{{ computedValueTextField }} <span v-if="appendWith">{{ appendWith }}</span></span
    >
  </div>
  <v-row v-else>
    <v-col class="d-flex" :xl="labelXl" :lg="labelLg" :md="labelMd" :sm="labelSm" :cols="labelSm" v-if="label">
      <v-label class="bold"
        ><span :class="isRequired ? 'required' : ''">{{ label }}</span
        >:</v-label
      >
    </v-col>
    <v-col :xl="textXl" :lg="textLg" :md="textMd" :sm="textSm" :cols="textSm">
      <v-row>
        <v-col cols="12">
          <v-text-field
            v-if="isEditable"
            :id="`dateField_${id}`"
            hide-details="auto"
            placeholder="MM/DD/YYYY"
            :aria-describedby="`dateField_${id}_Table`"
            :aria-label="ariaLabel"
            :disabled="isDisabled"
            class="pa-0 ma-0"
            :style="textStyle"
            v-model="computedValueTextField"
            @focus="isFocused = true"
            @blur="blur"
            :rules="
              isRequired
                ? [
                    (value) => {
                      return requiredRule(value);
                    },
                    (value) => {
                      return validDateFormatRule(value);
                    },
                    (value) => {
                      return validDateRangeRule(value, maximumDate, minimumDate);
                    },
                    (value) => {
                      return validDatesRule(value, invalidDates);
                    },
                  ]
                : [
                    (value) => {
                      return validDateFormatRule(value);
                    },
                    (value) => {
                      return validDateRangeRule(value, maximumDate, minimumDate);
                    },
                    (value) => {
                      return validDatesRule(value, invalidDates);
                    },
                  ]
            "
          >
            <template v-slot:append-inner>
              <DatePicker
                :maxDate="maximumDate"
                :minDate="minimumDate"
                v-model="computedValue"
                :required="isRequired"
                :invalidDates="invalidDates"
              />
            </template>
            <template v-if="appendWith" v-slot:append
              ><span>{{ appendWith }}</span></template
            >
          </v-text-field>
          <span v-else class="d-inline pl-4"
            >{{ computedValueTextField }} <span v-if="appendWith">{{ appendWith }}</span></span
          >
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { computed, watch, ref } from "vue";
import { requiredRule, validDateFormatRule, validDateRangeRule, validDatesRule } from "@/composables/validationRules";
import { getFormattedDateStringNoTime, checkValidDate } from "@/composables/dateField.js";
import DatePicker from "@/components/common/DatePicker.vue";
import dayjs from "dayjs";
export default {
  name: "LabelDatefield",
  props: {
    labelXl: { default: 3 },
    labelLg: { default: 4 },
    labelMd: { default: 5 },
    labelSm: { default: 6 },
    textXl: { default: 4 },
    textLg: { default: 5 },
    textMd: { default: 5 },
    textSm: { default: 6 },
    id: {},
    rules: { default: [] },
    label: { default: "" },
    textStyle: { default: "" },
    isEditable: { type: Boolean, default: false },
    isDisabled: { type: Boolean, default: false },
    noGrid: { type: Boolean, default: false },
    modelValue: { default: "" },
    minDate: { default: new Date("01/01/1901") },
    maxDate: { default: new Date("12/31/2099") },
    showTime: { type: Boolean, default: false },
    isRequired: { type: Boolean, default: false },
    tableDate: { type: Boolean, default: false },
    addLabelPadding: { type: Boolean, default: false },
    addDatePadding: { type: Boolean, default: false },
    appendWith: { default: null },
    invalidDates: { default: [] },
  },
  setup(props, context) {
    const minimumDate = computed({
      get: () => {
        if (props.minDate) {
          return new Date(props.minDate.toLocaleDateString());
        } else {
          return new Date("01/01/1901");
        }
      },
    });
    const maximumDate = computed({
      get: () => {
        if (props.maxDate) {
          return new Date(props.maxDate.toLocaleDateString());
        } else {
          return new Date("12/31/2099");
        }
      },
    });
    const ariaLabel = computed({
      get() {
        if (props.label == "" || !props.label) {
          return props.id + " has no label";
        } else {
          return props.label + "_" + props.id;
        }
      },
    });
    let showTimeDate = props.modelValue ? new Date(props.modelValue)?.toLocaleString() : null;
    let textValue = props.showTime ? showTimeDate : getFormattedDateStringNoTime(props.modelValue);
    let isFocused = ref(false);
    let updatedTextValue = false;
    let updateDatePicker = false;

    const blur = (e) => {
      isFocused.value = false;
      const value = e.target.value;
      textValue = value;
      if (updatedTextValue && checkValidDate(value)) {
        context.emit("update:modelValue", new Date(value));
      } else if (updatedTextValue) {
        context.emit("update:modelValue", value);
      }
    };
    const computedValueTextField = computed({
      get: () => {
        const value = props.showTime ? showTimeDate : getFormattedDateStringNoTime(props.modelValue);
        if (isFocused.value) {
          return textValue;
        } else {
          return props.modelValue && checkValidDate(textValue) ? value : textValue;
        }
      },
      set: (value) => {
        updatedTextValue = true;
        textValue = value;
      },
    });
    const computedValue = computed({
      get: () => {
        return props.modelValue && checkValidDate(textValue) ? new Date(dayjs(props.modelValue).toISOString()) : null;
      },

      set: (value) => {
        updateDatePicker = true;
        textValue = value ? getFormattedDateStringNoTime(value) : null;
        context.emit("update:modelValue", value);
      },
    });

    watch(
      () => [props.modelValue],
      () => {
        if (!updateDatePicker && !updatedTextValue) {
          showTimeDate = props.modelValue ? new Date(props.modelValue)?.toLocaleString() : null;
          const value = props.showTime ? showTimeDate : getFormattedDateStringNoTime(props.modelValue);
          textValue = props.modelValue ? value : null;
        } else {
          updateDatePicker = false;
          updatedTextValue = false;
        }
      },
      { deep: true }
    );
    return {
      validDatesRule,
      ariaLabel,
      maximumDate,
      minimumDate,
      validDateRangeRule,
      requiredRule,
      validDateFormatRule,
      blur,
      isFocused,
      computedValueTextField,
      checkValidDate,
      getFormattedDateStringNoTime,
      close,
      computedValue,
    };
  },
  components: { DatePicker },
};
</script>

<style scoped lang="scss">
.v-row {
  align-items: center;
}
.requiredText {
  font-size: 12px;
  color: rgb(215, 26, 23);
}
</style>
